import React, { useState, useEffect } from 'react';
import { useInventory } from '../../../contexts/InventoryContext';

const CategoryViewForm = ({ category, onClose, onEdit }) => {
  const { categories } = useInventory();
  const [parentCategory, setParentCategory] = useState(null);
  const [childCategories, setChildCategories] = useState([]);

  useEffect(() => {
    if (category) {
      // Find parent category if exists
      if (category.parent) {
        const parent = categories.find(cat => cat._id === category.parent);
        setParentCategory(parent);
      } else {
        setParentCategory(null);
      }

      // Find child categories
      const children = categories.filter(cat => cat.parent === category._id);
      setChildCategories(children);
    }
  }, [category, categories]);

  if (!category) return null;

  const getCategoryIcon = (cat) => {
    return cat.code.substring(0, 2).toUpperCase();
  };


  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="flex items-center justify-center min-h-full px-4 py-6">
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-white/20 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-white">View Category</h3>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 pt-3 pb-2">

            {/* Code + Name row */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Category Code</label>
                <div className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-gray-100 font-mono font-semibold text-gray-900">
                  {category.code}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Category Name</label>
                <div className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-gray-50 text-gray-900">
                  {category.name}
                </div>
              </div>
            </div>

            {/* Parent Category */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Parent Category</label>
              <div className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-gray-50 text-gray-900">
                {parentCategory ? (
                  `${parentCategory.name} (${parentCategory.code}) — Level ${parentCategory.level}`
                ) : (
                  <span className="text-gray-400 italic">Root Category (No parent)</span>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <div className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-gray-50 text-gray-900 min-h-[56px] whitespace-pre-wrap">
                {category.description || <span className="text-gray-400 italic">No description provided</span>}
              </div>
            </div>

            {/* Settings */}
            <div className="mb-3">
              <p className="text-xs font-medium text-gray-600 mb-1.5">Category Settings</p>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1.5">
                  <div className={`h-3 w-3 rounded border flex items-center justify-center ${category.requiresSerialNumber ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-gray-300'}`}>
                    {category.requiresSerialNumber && (
                      <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="text-xs text-gray-700">Serial Number Required</span>
                </label>
                <label className="flex items-center gap-1.5">
                  <div className={`h-3 w-3 rounded border flex items-center justify-center ${category.requiresExpiryDate ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-gray-300'}`}>
                    {category.requiresExpiryDate && (
                      <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="text-xs text-gray-700">Expiry Date Required</span>
                </label>
              </div>
            </div>

            {/* Additional Info */}
            {(childCategories.length > 0 || category.itemCount > 0 || category.createdAt) && (
              <div className="mb-3 pt-2 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-600 mb-1.5">Additional Information</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center justify-between px-2 py-1.5 bg-indigo-50 border border-indigo-100 rounded text-xs">
                    <span className="text-gray-600">Items</span>
                    <span className="font-semibold text-indigo-700">{category.itemCount || 0}</span>
                  </div>
                  <div className="flex items-center justify-between px-2 py-1.5 bg-purple-50 border border-purple-100 rounded text-xs">
                    <span className="text-gray-600">Level</span>
                    <span className="font-semibold text-purple-700">{category.level}</span>
                  </div>
                  {category.createdAt && (
                    <div className="px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-xs">
                      <span className="text-gray-400 block">Created</span>
                      <span className="text-gray-700">{new Date(category.createdAt).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  )}
                  {category.updatedAt && (
                    <div className="px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-xs">
                      <span className="text-gray-400 block">Updated</span>
                      <span className="text-gray-700">{new Date(category.updatedAt).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  )}
                </div>

                {childCategories.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-gray-600 mb-1">Child Categories ({childCategories.length})</p>
                    <div className="border border-gray-200 rounded divide-y divide-gray-100 max-h-32 overflow-y-auto">
                      {childCategories.map((child) => (
                        <div key={child._id} className="flex items-center justify-between px-2 py-1.5 hover:bg-gray-50">
                          <span className="text-xs font-medium text-gray-900">{child.name}</span>
                          <span className="text-[10px] text-gray-400">{child.code}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Footer Actions */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                Close
              </button>
              {onEdit && (
                <button
                  type="button"
                  onClick={() => { onEdit(category); onClose(); }}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded hover:from-indigo-700 hover:to-purple-700 transition-all"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Category
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryViewForm;
